// Implements: F-UIX-020, F-UIX-009, F-UIX-010, F-DSG-009
/**
 * The design profiles screen: the list on one side, the open profile in the editor.
 *
 * Two rules from checklist 14. The editor is mounted with `key` = what it shows
 * (`new` or the profile id), so no draft, refusal or pending state of one
 * profile leaks into another. And a save that answers after the person moved
 * on is ignored BY ID: it may only open the stored profile when the selection
 * is still the one the save was started from.
 *
 * Owner round 4: no per-scope card beside the list. Mockup v3 P5: where the open profile is active
 * is one switch per scope the person manages, in the editor, with the scopes the server says the
 * profile can serve (`activatable_in`); each row of the list shows four dots of its palette, read
 * from the listing itself (no detail read per row). With no profile of their own, the person sees
 * the list with the system profile (muted, opened read-only) and "Crear tu primer perfil".
 * Owner 2026-10-19: a deleted profile closes the editor, by id, only while it is still the one open.
 * Audit P5: a read-only profile the person opens from the list takes the focus to its notice.
 * Owner 2026-10-19 (answers 4A and 2A): "Duplicar y editar" is the server's instant copy, logo and
 * reference images included, on an editable profile (same scope) and on a read-only one (the
 * person's personal scope, so the copy joins their own list); the copy opens at once, by id: only
 * while the profile it was made from is still the one open.
 */

import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { TableSkeleton } from "../../../shared/ui/molecules/TableSkeleton";
import { ProfileList } from "../components/ProfileList";
import { useProfileListing } from "../hooks/useDesignProfiles";
import { isEditable } from "../model/profileModel";
import { scopeKey, scopeLabel } from "../model/scopeModel";
import type { ScopeWords } from "../model/scopeModel";
import { ProfileEditorContainer } from "./ProfileEditorContainer";
import type { CreatableScope } from "./ProfileEditorContainer";
import type { ActiveInScope } from "./ActiveInContainer";
import type { ProfileListingView, ProfileSummaryView } from "../../../types/design";
import styles from "./ProfileWorkspaceContainer.module.css";

/**
 * What the editor shows: nothing yet, a new profile (from the system default), or one stored profile
 * by id (`picked`: the person chose it in the list).
 */
type Selection =
  | { readonly kind: "none" }
  | { readonly kind: "new" }
  | {
      readonly kind: "profile";
      readonly id: string;
      readonly createdVersion: number | null;
      readonly picked?: boolean;
      /** A copy "Duplicar y editar" just made: its editor says so. */
      readonly duplicated?: boolean;
    };

const NONE: Selection = { kind: "none" };

/** What an address asks for: the profile it names, or nothing. The server still decides access. */
function requestedSelection(profileId: string | undefined): Selection {
  return profileId === undefined || profileId === ""
    ? NONE
    : { kind: "profile", id: profileId, createdVersion: null };
}

/** The id a selection stands for: `null` for a new profile (and for nothing open). */
function idOf(selection: Selection): string | null {
  return selection.kind === "profile" ? selection.id : null;
}

/**
 * Every scope of the listing as "Activo en" shows it for one profile: its name, whether the server
 * lists it in the profile's `activatable_in` and the readable name of another profile active there.
 * A profile the listing does not name can be activated nowhere.
 */
function activeInScopes(listing: ProfileListingView, profileId: string, words: ScopeWords): readonly ActiveInScope[] {
  const profiles: readonly ProfileSummaryView[] = listing.groups.flatMap((group) => group.profiles);
  const profile = profiles.find((item) => item.id === profileId);
  const activatable = (profile?.activatable_in ?? []).map(scopeKey);
  return listing.scopes.map((state) => {
    const other =
      state.active_profile_id === null || state.active_profile_id === profileId
        ? undefined
        : profiles.find((item) => item.id === state.active_profile_id);
    return {
      state,
      label: scopeLabel(state, words),
      allowed: activatable.includes(scopeKey(state)),
      otherName: other?.name ?? null,
    };
  });
}

export interface ProfileWorkspaceContainerProps {
  /**
   * The profile the address names (`?profile=`, where the indicator links), or none. It is
   * followed whenever it changes, so back and forward open what the address says.
   */
  readonly requestedProfileId?: string | undefined;
  /** A profile was opened from the screen (`null`: a new one), so the address can say so. */
  readonly onOpened?: ((profileId: string | null) => void) | undefined;
}

export function ProfileWorkspaceContainer({
  requestedProfileId,
  onOpened,
}: ProfileWorkspaceContainerProps = {}): ReactElement {
  const { t } = useTranslation();
  const listing = useProfileListing();
  const [selection, setSelectionState] = useState<Selection>(() => requestedSelection(requestedProfileId));
  const [followed, setFollowed] = useState(requestedProfileId);
  // The selection as of NOW, for a save whose answer arrives after a re-render.
  const current = useRef<Selection>(selection);
  useEffect(() => {
    current.current = selection;
  }, [selection]);
  const select = (next: Selection): void => {
    current.current = next;
    setSelectionState(next);
  };

  // The address changed (a link, back or forward): open what it names. An address without a
  // profile closes an open profile, never a profile still being created.
  if (followed !== requestedProfileId) {
    setFollowed(requestedProfileId);
    const next = requestedSelection(requestedProfileId);
    const differs =
      next.kind === "profile" ? idOf(selection) !== next.id : selection.kind === "profile";
    if (differs) setSelectionState(next);
  }

  if (listing.isError) {
    // The title says what failed; the generic state under it says why and offers another try.
    return (
      <ErrorState
        layout="page"
        title={t("design.list.failed")}
        error={listing.error}
        onRetry={() => {
          void listing.refetch();
        }}
      />
    );
  }
  if (listing.data === undefined) return <TableSkeleton />;

  const words = {
    personal: t("design.scope.personal"),
    team: t("design.scope.team"),
    organization: t("design.scope.organization"),
  };
  // Creation is offered in exactly the scopes the server says the person may edit (P6-OD-16),
  // personal first: the listing sorts by kind, and "personal" is what most people create.
  const creatable: readonly CreatableScope[] = listing.data.scopes
    .filter((scope) => scope.may_edit)
    .map((scope) => ({ value: scopeKey(scope), label: scopeLabel(scope, words), scope }))
    .sort(
      (left, right) =>
        Number(right.scope.scope_kind === "personal") - Number(left.scope.scope_kind === "personal"),
    );

  // A person who can create and has nothing of their own yet sees the one empty state of the
  // mockup, until they start the first profile (then the list and the editor appear together).
  const hasOwn = listing.data.groups.some((group) => group.profiles.some((profile) => isEditable(profile)));
  const startNew = (): void => {
    select({ kind: "new" });
    onOpened?.(null);
  };
  // Zero own profiles: the system profile is what the person sees (muted in the list, read-only).
  const shown: Selection =
    selection.kind === "none" && !hasOwn
      ? { kind: "profile", id: listing.data.system_default.id, createdVersion: null }
      : selection;
  const selectedId = idOf(shown);
  const systemDefaultId = listing.data.system_default.id;
  const activeScopesOf = (profileId: string | null): readonly ActiveInScope[] | undefined =>
    listing.data === undefined || profileId === null ? undefined : activeInScopes(listing.data, profileId, words);

  return (
    <div className={styles.workspace}>
      <div className={styles.list}>
        <ProfileList
          listing={listing.data}
          canCreate={creatable.length > 0}
          selectedId={selectedId}
          onSelect={(id) => {
            select({ kind: "profile", id, createdVersion: null, picked: true });
            onOpened?.(id);
          }}
          onNew={startNew}
          hideEmpty={selection.kind === "new"}
        />
      </div>
      {shown.kind === "none" ? null : (
        <ProfileEditorContainer
          key={selectedId ?? "new"}
          profileId={selectedId}
          seedProfileId={listing.data.system_default.id}
          focusNotice={shown.kind === "profile" && shown.picked === true}
          onDuplicated={(sourceId, copyId) => {
            // An answer for a profile the person already left opens nothing (checklist 14). With
            // nothing picked, the profile on screen is the system default the screen showed itself.
            const now = current.current;
            const open = now.kind === "none" ? systemDefaultId : idOf(now);
            if (open !== sourceId) return;
            select({ kind: "profile", id: copyId, createdVersion: null, duplicated: true });
            onOpened?.(copyId);
          }}
          duplicated={shown.kind === "profile" && shown.duplicated === true}
          creatableScopes={creatable}
          announcedVersion={shown.kind === "profile" ? shown.createdVersion : null}
          activeScopes={activeScopesOf(selectedId)}
          onDeleted={(deletedId) => {
            if (idOf(current.current) !== deletedId) return;
            select(NONE);
            onOpened?.(null);
          }}
          onSaved={(requestedFor, savedId, version) => {
            const now = current.current;
            // Only a creation opens what it stored, and only while "new" is still open.
            if (requestedFor !== null || now.kind !== "new") return;
            select({ kind: "profile", id: savedId, createdVersion: version });
            onOpened?.(savedId);
          }}
        />
      )}
    </div>
  );
}
